import { ErrorInfo, useState } from 'react'

import {
  ContextMenu,
  ContextMenuOptionColors,
  ContextMenuTriggerOnActions
} from '@inno-ui/context-menu'
import { MultiplyIcon } from '@inno-ui/icons'
import { useRemoveWidget } from 'modules/desktop/pages/MainPage/hooks/useRemoveWidget'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { captureReactError } from 'shared/services/logger'

import { WidgetComponent } from '../../../types/widgets.types'
import { WidgetWrapper } from '../../WidgetWrapper'
import styles from '../WidgetsErrorBoundary.module.scss'
import { ErrorBoundaryProps } from '../WidgetsErrorBoundary.types'
import { DefaultErrorFallback } from './DefaultErrorFallback'

export const ErrorBoundaryWrapper: WidgetComponent<ErrorBoundaryProps> = ({
  children,
  orientation = 'vertical',
  isSidebarWidget = false,
  widgetId,
  ...widgetProps
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets' })
  const removeWidget = useRemoveWidget()
  const [resetKey, setResetKey] = useState(0)

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    captureReactError(error, errorInfo)
  }

  const fallbackRender = (fallbackProps: FallbackProps) => {
    const wrappedResetErrorBoundary = () => {
      setResetKey(prev => prev + 1)
      fallbackProps.resetErrorBoundary()
    }

    const defaultErrorFallback = (
      <WidgetWrapper {...widgetProps}>
        <DefaultErrorFallback
          {...fallbackProps}
          resetErrorBoundary={wrappedResetErrorBoundary}
          orientation={orientation}
        />
      </WidgetWrapper>
    )

    // a widget stuck in a permanent crash loop has no other way to be
    // removed, since the crashed content itself can't render its own
    // context-menu-triggering chrome.
    if (!isSidebarWidget) {
      return defaultErrorFallback
    }

    return (
      <ContextMenu
        triggerNode={defaultErrorFallback}
        triggerWrapperClassName={styles.triggerWrapper}
        triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
        options={[
          {
            icon: MultiplyIcon,
            label: t('remove_widget'),
            color: ContextMenuOptionColors.Red,
            onClick: () => {
              if (widgetId) {
                removeWidget(widgetId)
              }
            }
          }
        ]}
        showAtClickPoint
        zIndex={3}
        stopEventPropagation
      />
    )
  }

  return (
    <ErrorBoundary
      onError={handleError}
      fallbackRender={fallbackRender}
      resetKeys={[resetKey]}
    >
      {children}
    </ErrorBoundary>
  )
}
